"use client";

import { Button, cn, Image } from "@scott/ui/client";
import { Home } from "lucide-react";

export type ErrorPageProps = {
  errorCode?: "403" | "404" | "500";
  title?: string;
  description?: string[];
  buttonText?: string;
  onButtonClick?: () => void;
  imageSrc?: string;
  imageAlt?: string;
  className?: string;
};

export function ErrorPage(props: ErrorPageProps) {
  const {
    errorCode,
    title,
    description,
    buttonText,
    onButtonClick,
    imageSrc = "/assets/404-500-background.jpg",
    imageAlt = "Error page background",
    className,
  } = props;

  // Substring length for unique key generation (prevent collision for similar lines)
  const KEY_SUBSTRING_LENGTH = 20;

  return (
    <div
      className={cn(
        "relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100",
        className
      )}
    >
      {/* Background Image - Absolute positioning to fill container with overlay */}
      <div className="absolute inset-0">
        <Image alt={imageAlt} aspectRatio="16:9" fill priority src={imageSrc} />
        <div className="absolute inset-0 bg-black/10" />
      </div>

      {/* Content Box - Enhanced with better spacing and design */}
      <div className="relative z-10 mx-4 w-full max-w-2xl">
        <div className="rounded-2xl border border-gray-200/50 bg-white/95 p-8 shadow-2xl backdrop-blur-md md:p-12 lg:p-16">
          <div className="flex flex-col items-center space-y-8 text-center">
            {/* Error Code with enhanced styling */}
            <div className="space-y-4">
              <div className="inline-flex items-center justify-center rounded-full bg-gradient-to-br from-red-50 to-orange-50 p-6">
                <h1 className="bg-gradient-to-br from-red-600 to-orange-600 bg-clip-text font-extrabold text-8xl text-transparent md:text-9xl">
                  {errorCode}
                </h1>
              </div>

              {/* Title with better typography */}
              <h2 className="font-bold text-3xl text-gray-900 md:text-4xl">
                {title}
              </h2>
            </div>

            {/* Description with improved readability */}
            {description ? (
              <div className="space-y-3">
                {description.map((line, index) => (
                  <p
                    className="text-gray-600 text-lg leading-relaxed md:text-xl"
                    key={`line-${index}-${line.substring(0, KEY_SUBSTRING_LENGTH)}`}
                  >
                    {line}
                  </p>
                ))}
              </div>
            ) : null}

            {/* Enhanced button with icon */}
            {buttonText && onButtonClick ? (
              <Button
                className="group mt-4 flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-4 font-semibold text-white shadow-lg transition-all duration-200 hover:scale-105 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl"
                onClick={onButtonClick}
                size="lg"
              >
                <Home className="group-hover:-translate-y-0.5 h-5 w-5 transition-transform" />
                {buttonText}
              </Button>
            ) : null}
          </div>
        </div>

        {/* Decorative elements */}
        <div className="-left-4 -top-4 absolute h-24 w-24 animate-pulse rounded-full bg-blue-500/10 blur-2xl" />
        <div className="-bottom-4 -right-4 absolute h-32 w-32 animate-pulse rounded-full bg-orange-500/10 blur-2xl" />
      </div>
    </div>
  );
}
